<template>
  <div class="modal-container">
    <div class="wraper-content">
      <div class="position-btn-close ">
        <div class="close-box-btn">
          <AppIcon name="close" width="16" @click="closeModal" />
        </div>
      </div>
      <div class="wraper-title">
        <h1 class="header">
          {{ $t('_web_create-account-modal', 'Create an account') }}
        </h1>
        <h3 class="text-modal">
          {{ $t('_web_already_have', 'Already have an account?') }}
          <a class="login-hover" @click="openLoginModal">
            {{ $t('_web_already_login', ' Login') }}</a
          >
        </h3>
      </div>
      <form
        class="form-input"
        @submit.prevent="formSubmit"
        @input="clearCustomError"
      >
        <InputUsername
          v-model.trim="login"
          v-validate="'required|usernamePattern'"
          :placeholder="$t('_web_username_placeholder', 'Username')"
          :name="$t('_web_tournament_players_column-username', 'username')"
          type="text"
          variant=""
          autocomplete-off
          :tabindex="0"
          required
          :label="$$t('Username', 'username-label')"
          :error="
            errors.first(
              this.$t('_web_tournament_players_column-username', 'username')
            )
          "
          @input="
            value =>
              debounceCredentials(
                this.$t('_web_tournament_players_column-username', 'username'),
                value
              )
          "
        />
        <InputEmail
          v-model.trim="email"
          v-focus
          v-validate="'required|email|max:128'"
          :placeholder="$t('_web_email_placeholder', 'your@email.com')"
          :name="this.$$t('Email', 'email-label')"
          type="email"
          :label="$$t('Email', 'email-label')"
          :tabindex="0"
          autofocus
          variant=""
          required
          :error="errors.first(this.$$t('Email', 'email-label'))"
          @input="
            value =>
              debounceCredentials(this.$$t('Email', 'email-label'), value)
          "
        />
        <InputPassword
          v-model.trim="password"
          v-validate="'required|min:8|max:64'"
          :placeholder="$t('_web_password_placeholder', 'Password')"
          :name="$t('_web_password_placeholder', 'Password')"
          :tabindex="0"
          autocomplete-off
          variant=""
          required
          :label="$t('_web_choose_password', 'Choose your password')"
          :error="errors.first($t('_web_password_placeholder', 'Password'))"
        />

        <HeraFormCheckbox
          v-model="agreements"
          v-validate="'required'"
          class="new-checkbox-modal m-0"
          :name="$t('_web_signup_agreement', 'agreement')"
          :tabindex="0"
          variant=""
          :error="errors.first($t('_web_signup_agreement', 'agreement'))"
          :val="true"
        >
          <TextWithLinks
            :content="
              $t(
                '_web_i-agree-privacy-terms',
                'I agree to {:privacy} and {:terms}'
              )
            "
            :content-props="agreementsTextProps"
            class="check-box-links"
          />
        </HeraFormCheckbox>

        <invisible-recaptcha
          class="text-truncate new-signup-btn"
          :sitekey="recaptchaKey"
          :badge="'top'"
          :validate="() => formSubmit"
          :callback="formSubmit"
          :disabled="submitIsDisabled"
          :loading="isLoading"
          >Register
        </invisible-recaptcha>
      </form>
      <div class="d-flex row justify-content-center w-100 align-items-center">
        <GoogleSignIn />
        <TwitterSignIn />
        <AppleSignIn />
      </div>
    </div>
    <ModalBackdrop
      v-model="isLoading"
      class="sign-up-loader"
      :style="{ width: `100%` }"
      :click_on_backdrop="false"
      :esc_key="false"
      :variant="'loader'"
    >
      <img
        src="~@/assets/images/playhera-indo/Playhera_Loader.svg"
        alt="Loader"
      />
    </ModalBackdrop>
  </div>
</template>
<script>
import { Validator } from 'vee-validate';
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { translate } from '@/mixins/translate';
import AppIcon from '@/components/atoms/Icon';

import isLoading from '@/mixins/isLoading.js';
import {
  PRIVACY_LINK,
  TERMS_LINK,
  RECAPTCHA_KEY,
  GOOGLE_CLIENT_ID,
  FIREBASE_API_KEY,
  FIREBASE_PROJECT_ID,
} from '@/api/config';
import InputEmail from '@/components/form/NewInputEmail';
import InputUsername from '@/components/form/NewInputUsername';
import InputPassword from '@/components/form/NewInputPassword';
import HeraFormCheckbox from '@/components/form/NewHeraFormCheckBox';
import TextWithLinks from '@/components/text/TextWithLinks';
import GoogleSignIn from '@/components/auth/GoogleSignIn';
import TwitterSignIn from '@/components/auth/TwitterSignIn';
import AppleSignIn from '@/components/auth/AppleSignIn';
import InvisibleRecaptcha from 'vue-invisible-recaptcha';
import ModalBackdrop from '@/components/common/ModalBackdrop.vue';

const config = {
  usernameRegex: /^[a-z0-9_]+$/i,
};
const DEBOUNCE_TIME = 500; // ms
let routerNameFrom;

export default {
  name: 'SignUpModal',
  components: {
    InputEmail,
    InputUsername,
    InputPassword,
    HeraFormCheckbox,
    TextWithLinks,
    GoogleSignIn,
    TwitterSignIn,
    AppleSignIn,
    ModalBackdrop,
    AppIcon,

    'invisible-recaptcha': InvisibleRecaptcha,
  },
  inject: ['$validator'],
  mixins: [isLoading, translate('_web_signup')],

  data() {
    return {
      email: null,
      recaptchaKey: RECAPTCHA_KEY,
      login: null,
      password: null,
      country: null,
      agreements: [],
      agreementsTextProps: {
        terms: {
          text: 'Terms',
          href: TERMS_LINK,
        },

        privacy: {
          text: 'Privacy',
          href: PRIVACY_LINK,
        },
      },
      haveAccountTextProps: {
        login: {
          text: this.$$t('Login'),
          to: {
            name: 'signin',
          },
        },
      },
      debounceCredentials: this.$lodash.debounce(
        this.checkCredentials,
        DEBOUNCE_TIME
      ),
    };
  },

  computed: {
    ...mapGetters(['templateRegData']),
    formData() {
      return {
        email: this.email,
        login: this.login,
        password: this.password,
        country: 'ID' && 'Indonesia',
        by: this.$route.name,
      };
    },
    firebaseConfigured() {
      return Boolean(FIREBASE_API_KEY && FIREBASE_PROJECT_ID);
    },
    googleConfigured() {
      return Boolean(GOOGLE_CLIENT_ID);
    },
    submitIsDisabled() {
      return !!(
        this.$lodash.get(this, 'errors.items.length', 0) || // validator has not error
        Object.values(this.formData).some(value => value === null) || // all items of form is not empty
        !this.agreements[0]
      ); // `I agree to ...`
    },
    customError() {
      return this.errors.first('customError');
    },
  },
  watch: {
    '$route.name'() {
      this.$store.dispatch('closeModal');
    },
  },

  mounted() {
    if (routerNameFrom !== 'verifyCode') {
      this.saveRegData(null);
    } else {
      const { templateRegData } = this;

      if (templateRegData && templateRegData.by === this.$route.name) {
        this.password = null;
        this.email = templateRegData.email || '';
        this.login = templateRegData.login || '';

        this.country = { id: 'ID', text: 'Indonesia', value: 'Indonesia' };
        this.agreements = [!!templateRegData.agreements];
      }
    }
    this.errors.clear();
    if (routerNameFrom !== 'verifyCode') {
      this.saveRegData(null);
    } else {
      const { templateRegData } = this;

      if (templateRegData && templateRegData.by === this.$route.name) {
        this.password = null;
        this.email = templateRegData.email || '';
        this.login = templateRegData.login || '';

        this.country = { id: 'ID', text: 'Indonesia', value: 'Indonesia' };
        this.agreements = [!!templateRegData.agreements];
      }
    }
  },
  created() {
    Validator.extend('usernamePattern', {
      getMessage: this.$t(
        '_web_signup_username_pattern_error',
        'Username must contain only next symbols: {symbols} and {underline}',
        { symbols: '(0-9), (a-z), (A-Z)', underline: '_' }
      ),
      validate: value => config.usernameRegex.test(value),
    });
  },

  methods: {
    ...mapMutations(['saveRegData']),

    ...mapActions(['createUser', 'getCredentials', 'openModal', 'closeModal']),
    openLoginModal() {
      this.closeModal();
      this.openModal({
        name: 'SignInModal',
        settings: {
          clickOnBackdrop: false,
          escKey: false,
          variant: 'transparent',
        },
      });
    },

    formSubmit(token) {
      const captcha_response = token;
      if (!this.errors.count()) {
        this.setLoading();

        this.$validator
          .validateAll()
          .then(result =>
            result ? Promise.resolve(this.formData) : Promise.reject(result)
          )
          .then(formData => {
            return this.createUser({ ...formData, captcha_response });
          })
          .then(res => {
            this.unsetLoading();
            // this.closeModal(),
            this.openModal({
              name: 'CodeVerifyModal',
              settings: {
                variant: 'transparent',
                escKey: false,
                clickOnBackdrop: false,
              },
              props: {
                email: this.formData.email,
                tokenToSend: res.temporary_token,
                from: 'signup',
              },
            });
          })
          .then(
            window.dataLayer.push({
              event: 'signup',
              username: this.login,
              email: this.email,
              country: this.country.value,
              phone: this.phone,
              from: this.$route.path,
            })
          )
          .catch(error => {
            this.unsetLoading();
            this.$validator.errors.add({
              field: 'customError',
              msg: error,
              rule: 'custom',
              id: 1,
            });
          })
          .finally(this.unsetLoading(), (this.isLoading = false));
      }
    },

    checkCredentials(field, value) {
      this.$validator.validate(field).then(isValid => {
        if (!isValid) {
          return;
        }
        this.getCredentials(value).then(() => {
          this.$validator.errors.add({
            field,
            msg: `${this.$t('credentials-error', {
              field: field,
            })}`,
            rule: 'custom',
          });
        });
      });
    },
    clearCustomError() {
      this.errors.remove('customError');
    },
  },
};
</script>
<style lang="scss" scoped>
.modal-container {
  .close-box-btn {
    cursor: pointer;
    border-radius: 90%;
    border: 1px solid black;
    width: 48px;
    height: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #30374a;
    background-color: #181c25;
  }
}
.modal-container {
  .position-btn-close {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    padding-top: 20px;
  }
}
.modal-container {
  border-radius: 16px;
  background: #151520 !important;
  min-height: 40dvh;
  max-height: 90dvh;
  flex-direction: column;
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-family: 'Alexandria', sans-serif;
  max-width: 625px;
  min-width: 625px;
  overflow: auto;
  @media (max-width: 915px) {
    max-width: 525px;
    min-width: 525px;
    min-height: 600px;
  }
  @media (max-width: 600px) {
    max-width: 455px;
    min-width: 455px;
  }

  @media (max-width: 450px) {
    max-width: 325px;
    min-width: 325px;
    max-height: 100dvh;
    overflow-y: scroll;
  }
  @media (max-height: 730px) {
    overflow-y: scroll;
    justify-content: flex-start;
  }
  > .wraper-content {
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    padding: 40px 32px;
    min-width: 84%;
    gap: 20px;

    @media (max-width: 600px) {
      padding: 20px 20px;
    }

    > .wraper-title {
      display: flex;
      flex-direction: column;
      gap: 4px;
      > .header {
        margin: 0;
        font-size: 32px;
        font-style: normal;
        font-weight: 600;
        line-height: 135%; /* 43.2px */
        letter-spacing: -0.48px;
        background: var(
          --Title,
          linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.5) 5.29%,
            rgba(255, 255, 255, 0.91) 51.55%,
            rgba(255, 255, 255, 0.5) 100%
          )
        );
        background-clip: content-box;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }
      > .text-modal {
        font-size: 14px;
        margin: 0;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
        color: rgba(255, 255, 255, 0.5);
        text-align: center;
        > a {
          color: var(--opacity-700, rgba(255, 255, 255, 0.8));
          cursor: pointer;
          &:hover {
            color: #5a58f2;
            text-decoration: none;
          }
        }
      }
    }
    > form.form-input {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      align-self: stretch;
      width: 100%;
      > .new-checkbox-modal {
        width: 100%;
        min-width: none;
      }
      > .new-signup-btn {
        background-color: white !important;
        display: flex;
        padding: 16px;
        justify-content: center;
        align-items: center;
        gap: 12px;
        align-self: stretch;
        color: #121217 !important;
        text-align: center;
        /* Body/Body-16/Medium */
        font-family: 'Alexandria', sans-serif;
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 24px;
      }
    }
  }
}
.sign-up-loader {
  width: 100vw !important;
  > .modalbackdrop_container {
    background: none !important;
    background-color: transparent !important;
  }
}
</style>
